/**
 * TripContextDrawer - Collapsible panel showing trip context summary
 *
 * Shows trip name, destination, dates, and entity counts.
 * Collapsed by default; expands on user interaction.
 * Loads entity counts from Firestore using effectiveUserId pattern.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Hotel as HotelIcon,
  CalendarToday as CalendarIcon,
  Place as PlaceIcon,
  Flight as FlightIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { db } from '@trip-planner-monorepo/shared';
import { collection, getCountFromServer } from 'firebase/firestore';

/**
 * TripContextDrawer Component
 *
 * @param {Object} props
 * @param {Object} props.currentTrip - Current trip data
 * @param {string} props.effectiveUserId - Trip owner's user ID
 * @param {string} props.tripId - Trip identifier
 * @param {Function} [props.onReviewTrip] - Called when user clicks the ✨ Review Trip button
 */
const TripContextDrawer = ({ currentTrip, effectiveUserId, tripId, onReviewTrip }) => {
  const [expanded, setExpanded] = useState(false);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded || !effectiveUserId || !tripId) return;
    if (counts !== null) return; // Already loaded

    setLoading(true);

    const loadCounts = async () => {
      try {
        const base = ['users', effectiveUserId, 'trips', tripId];
        const subcollections = [
          { key: 'accommodation', path: [...base, 'accommodation'] },
          { key: 'days', path: [...base, 'days'] },
          { key: 'places', path: [...base, 'places'] },
          { key: 'travel', path: [...base, 'travel'] }
        ];

        const results = await Promise.allSettled(
          subcollections.map(async ({ key, path }) => {
            const ref = collection(db, ...path);
            const snap = await getCountFromServer(ref);
            return { key, count: snap.data().count };
          })
        );

        const newCounts = {};
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            newCounts[result.value.key] = result.value.count;
          }
        });

        setCounts(newCounts);
      } catch (error) {
        console.error('[TripContextDrawer] Error loading counts:', error);
        setCounts({});
      } finally {
        setLoading(false);
      }
    };

    loadCounts();
  }, [expanded, effectiveUserId, tripId, counts]);

  // Guard on tripId (always available when a trip is selected).
  // currentTrip may briefly be null while loading — we still show the bar with ✨ Review Trip.
  if (!tripId) return null;

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const d = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return String(dateValue);
    }
  };

  const startDate = formatDate(currentTrip?.startDate);
  const endDate = formatDate(currentTrip?.endDate);

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fafafa'
      }}
    >
      {/* Collapsed header - always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: '#f0f0f0' }
        }}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse trip context' : 'Expand trip context'}
      >
        <InfoIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontWeight: 500 }}>
          Trip Context: {currentTrip?.name || 'Loading...'}
          {currentTrip?.destination ? ` — ${currentTrip.destination}` : ''}
        </Typography>

        {/* ✨ Review Trip button — triggers a full AI review as a chat message */}
        {onReviewTrip && (
          <Box
            component="button"
            onClick={(e) => {
              e.stopPropagation(); // don't toggle the drawer
              onReviewTrip();
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              mr: 0.75,
              px: 1,
              py: 0.25,
              fontSize: '0.68rem',
              fontWeight: 500,
              fontFamily: 'inherit',
              color: '#b45309',           // amber-700
              backgroundColor: '#fef3c7', // amber-100
              border: '1px solid #fcd34d', // amber-300
              borderRadius: '12px',
              cursor: 'pointer',
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              transition: 'background-color 0.15s, border-color 0.15s',
              '&:hover': {
                backgroundColor: '#fde68a', // amber-200
                borderColor: '#f59e0b',      // amber-400
              },
              '&:active': {
                backgroundColor: '#fcd34d',
              }
            }}
            title="Ask your travel assistant to review your trip"
          >
            ✨ Review Trip
          </Box>
        )}

        <IconButton size="small" tabIndex={-1}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expanded details */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Trip info */}
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {currentTrip?.name || 'Loading...'}
          </Typography>

          {currentTrip?.destination && (
            <Typography variant="caption" color="text.secondary" display="block">
              {currentTrip.destination}
            </Typography>
          )}

          {(startDate || endDate) && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {startDate && endDate
                ? `${startDate} — ${endDate}`
                : startDate || endDate}
            </Typography>
          )}

          {/* Entity counts */}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Loading trip data...
              </Typography>
            </Box>
          ) : counts && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
              {counts.accommodation != null && (
                <Chip
                  icon={<HotelIcon />}
                  label={`${counts.accommodation} accommodation`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {counts.days != null && (
                <Chip
                  icon={<CalendarIcon />}
                  label={`${counts.days} days`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {counts.places != null && (
                <Chip
                  icon={<PlaceIcon />}
                  label={`${counts.places} places`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {counts.travel != null && (
                <Chip
                  icon={<FlightIcon />}
                  label={`${counts.travel} travel`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TripContextDrawer;
